// TopBar (DESIGN §2), 56px: breadcrumb (from the route + store names) · theme toggle · refresh · bell · avatar menu.
// <768px: logo mark · current page only · search icon · bell · avatar (theme lives in the avatar menu there).
import './topbar.css';
import { useState } from 'preact/hooks';
import { crumbsFor, href } from '../router.js';
import { reload, toggleTheme, useStore } from '../store.js';
import { Icon } from '../icons.jsx';
import { NotificationsButton } from './notifications.jsx';
import { AvatarMenu } from './person-switcher.jsx';
import { openSearch } from './search.jsx';
import { LogoMark } from './sidebar.jsx';
import { cx } from './ui.jsx';

// items: [{ label, href?, title? }] — the last one is the current page.
export function Breadcrumbs({ items = [], class: cls }) {
  return (
    <nav class={cx('breadcrumb', cls)} aria-label="Breadcrumb">
      <ol>
        {items.map((item, i) => {
          const last = i === items.length - 1;
          return (
            <li key={i} class={cx('breadcrumb-item', last && 'is-current')}>
              {item.href && !last
                ? <a href={item.href} title={item.title || item.label}>{item.label}</a>
                : <span aria-current={last ? 'page' : undefined} title={item.title || item.label}>{item.label}</span>}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function ThemeToggle({ class: cls }) {
  const { theme } = useStore();
  const next = theme === 'light' ? 'dark' : 'light';
  return (
    <button type="button" class={cx('icon-btn', cls)} onClick={toggleTheme} aria-label={`Switch to ${next} theme`} title={`${next === 'dark' ? 'Dark' : 'Light'} theme`}>
      <Icon name={theme === 'light' ? 'moon' : 'sun'} size={18} />
    </button>
  );
}

export function RefreshButton({ class: cls }) {
  const [busy, setBusy] = useState(false);
  const run = () => {
    if (busy) return;
    setBusy(true);
    Promise.resolve(reload()).catch(() => {}).finally(() => setBusy(false));
  };
  return (
    <button type="button" class={cx('icon-btn', busy && 'is-busy', cls)} onClick={run} aria-label="Refresh data" title="Refresh" disabled={busy}>
      <Icon name="refresh" size={18} class="refresh-icon" />
    </button>
  );
}

export function TopBar({ route }) {
  const { index } = useStore();
  const crumbs = crumbsFor(route, index);
  return (
    <header class="topbar">
      <a class="topbar-logo" href={href('/')} aria-label="FRAZIER — Overview"><LogoMark size={26} /></a>
      {/* Phones: a person's page names itself in its title, so the top bar offers the way back instead. */}
      {route.name === 'person' && <a class="topbar-back" href={href('/people')}><Icon name="chevronLeft" size={18} />People</a>}
      <Breadcrumbs items={crumbs} class={route.name === 'person' ? 'has-back' : undefined} />
      <div class="topbar-actions">
        <button type="button" class="icon-btn topbar-search" onClick={() => openSearch()} aria-label="Search" title="Search">
          <Icon name="search" size={18} />
        </button>
        <ThemeToggle class="topbar-wide" />
        <RefreshButton class="topbar-wide" />
        <NotificationsButton />
        <AvatarMenu />
      </div>
    </header>
  );
}

// Document title for the route: "<current page> · FRAZIER".
export function documentTitle(route, index) {
  const crumbs = crumbsFor(route, index);
  const last = crumbs[crumbs.length - 1];
  const label = last?.title && route.name === 'batch' ? last.title : last?.label;
  return label ? `${label} · FRAZIER` : 'FRAZIER';
}
